import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Calculator, CheckCircle2, XCircle, Loader2, Save, PlugZap, Eye, EyeOff,
  HelpCircle, ChevronDown, ChevronUp, ExternalLink,
} from 'lucide-react';

export default function PennylaneSettingsCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [data, setData] = useState(null);
  const [showToken, setShowToken] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [lastTest, setLastTest] = useState(null); // 'ok' | 'ko'
  const [lastTestMsg, setLastTestMsg] = useState('');
  const [form, setForm] = useState({
    api_token: '',
    is_sandbox: true,
    default_purchase_ledger: '606300',
    default_vat_account: '445660',
    default_revenue_ledger: '706000',
  });

  const load = async () => {
    try {
      const r = await api.get('/pennylane/settings');
      setData(r.data);
      setForm((p) => ({
        ...p,
        is_sandbox: r.data.is_sandbox,
        default_purchase_ledger: r.data.default_purchase_ledger || '606300',
        default_vat_account: r.data.default_vat_account || '445660',
        default_revenue_ledger: r.data.default_revenue_ledger || '706000',
      }));
    } catch (e) {
      // Non-admin or not yet configured: stay silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        is_sandbox: form.is_sandbox,
        default_purchase_ledger: form.default_purchase_ledger,
        default_vat_account: form.default_vat_account,
        default_revenue_ledger: form.default_revenue_ledger,
      };
      if (form.api_token) body.api_token = form.api_token;
      await api.put('/pennylane/settings', body);
      toast.success('Paramètres Pennylane enregistrés');
      setForm((p) => ({ ...p, api_token: '' }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setLastTest(null);
    try {
      const r = await api.post('/pennylane/test-connection');
      const scopes = (r.data.scopes || []).join(', ');
      setLastTest('ok');
      setLastTestMsg(`Connexion Pennylane OK${r.data.company ? ' (' + r.data.company + ')' : ''} — scopes : ${scopes || 'non listés'}`);
      toast.success('Connexion Pennylane OK');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Échec de la connexion Pennylane';
      setLastTest('ko');
      setLastTestMsg(msg);
      toast.error(msg, { duration: 10000 });
    }
    setTesting(false);
  };

  if (loading || !data) return null;
  const configured = data.configured;

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200" data-testid="pennylane-settings-card">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
            <Calculator className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Pennylane (Comptabilité)</h2>
            <div className="flex items-center gap-2 mt-1">
              {configured ? (
                <><CheckCircle2 className="w-4 h-4 text-green-600" /><span className="text-sm text-green-600 font-medium">Configuré</span></>
              ) : (
                <><XCircle className="w-4 h-4 text-slate-400" /><span className="text-sm text-slate-500">Non configuré</span></>
              )}
              <Badge variant="outline" className="text-xs">{form.is_sandbox ? 'Sandbox' : 'PRODUCTION'}</Badge>
            </div>
          </div>
        </div>
        <Button variant="outline" onClick={handleTest} disabled={testing || !configured} data-testid="pennylane-test-btn">
          {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlugZap className="w-4 h-4 mr-2" />}
          Tester la connexion
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Environnement</label>
          <select
            value={form.is_sandbox ? 'sandbox' : 'production'}
            onChange={(e) => setForm((p) => ({ ...p, is_sandbox: e.target.value === 'sandbox' }))}
            className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
          >
            <option value="sandbox">Sandbox (tests)</option>
            <option value="production">Production</option>
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
            Token API Pennylane {data.api_token_set && <span className="normal-case font-normal text-slate-400">(défini — laissez vide pour conserver)</span>}
          </label>
          <div className="relative">
            <Input
              type={showToken ? 'text' : 'password'}
              value={form.api_token}
              onChange={(e) => setForm((p) => ({ ...p, api_token: e.target.value }))}
              placeholder={data.api_token_set ? '••••••••' : 'Coller votre Company API Token'}
              data-testid="pennylane-token-input"
            />
            <button type="button" onClick={() => setShowToken((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Compte achats (60x) <span className="normal-case font-normal text-slate-400">par défaut</span></label>
          <Input value={form.default_purchase_ledger} onChange={(e) => setForm((p) => ({ ...p, default_purchase_ledger: e.target.value }))} placeholder="606300" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">TVA déductible (4456)</label>
          <Input value={form.default_vat_account} onChange={(e) => setForm((p) => ({ ...p, default_vat_account: e.target.value }))} placeholder="445660" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Compte ventes (706)</label>
          <Input value={form.default_revenue_ledger} onChange={(e) => setForm((p) => ({ ...p, default_revenue_ledger: e.target.value }))} placeholder="706000" />
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between gap-3">
        <p className="text-xs text-slate-500">
          Pennylane est requis en plan <b>Essentiel</b> ou supérieur. Le token et les comptes sont chiffrés en base.
        </p>
        <Button onClick={handleSave} disabled={saving} data-testid="pennylane-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>

      {lastTest && (
        <div className={`mt-4 rounded-lg border p-3 text-sm ${lastTest === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-red-50 border-red-200 text-red-900'}`}>
          <div className="flex items-start gap-2">
            {lastTest === 'ok' ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 shrink-0" />}
            <span className="flex-1 whitespace-pre-wrap break-words">{lastTestMsg}</span>
          </div>
        </div>
      )}

      <div className="mt-4 border-t border-slate-200 pt-3">
        <button type="button" onClick={() => setShowHelp((v) => !v)} className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-700">
          <HelpCircle className="w-4 h-4" />
          Comment obtenir mon token Pennylane ?
          {showHelp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showHelp && (
          <div className="mt-3 bg-blue-50/60 border border-blue-200 rounded-lg p-4 text-sm text-slate-700 space-y-2">
            <ol className="list-decimal list-inside space-y-1 text-slate-700">
              <li>Connectez-vous sur <a href="https://app.pennylane.com" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">app.pennylane.com <ExternalLink className="w-3 h-3" /></a></li>
              <li>Allez dans <b>Paramètres &gt; Connectivité &gt; Développeurs</b></li>
              <li>Cliquez sur <b>« Générer un token API »</b></li>
              <li>Cochez tous les scopes <b>:all</b> nécessaires : <code className="bg-white px-1 rounded text-xs">customers:all</code>, <code className="bg-white px-1 rounded text-xs">suppliers:all</code>, <code className="bg-white px-1 rounded text-xs">customer_invoices:all</code>, <code className="bg-white px-1 rounded text-xs">supplier_invoices:all</code>, <code className="bg-white px-1 rounded text-xs">file_attachments:all</code></li>
              <li>Choisissez une expiration (1 an conseillé) et copiez le token IMMÉDIATEMENT (il ne sera plus jamais affiché)</li>
              <li>Collez-le ci-dessus puis cliquez sur <b>Enregistrer</b> et <b>Tester la connexion</b></li>
            </ol>
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
              💡 Pour tester sans risque, créez d'abord un environnement <b>sandbox</b> dans Pennylane (Profil &gt; « Environnement de test »).
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
